import React, { useEffect, useMemo } from 'react';
import { type TimeSlotAvailability } from '../../../models/booking/TimeSlotAvailability';
import { useBookingContext } from '../BookingContext';
import { addHoursToTime, formatLocalDate } from '../../../utils/time';
import { formatUsTime } from '../../../utils/bookingUtils';
import BookingCalendar from '../shared/BookingCalendar';

const today = new Date();

interface DateTimeSectionProps {
  slots: TimeSlotAvailability[];
  isLoadingSlots: boolean;
  slotsError: string;
  bookableDates: Set<string> | null; // null = unknown/failed → no disabling
  onViewMonthChange?: (year: number, month: number) => void;
}

const DateTimeSection: React.FC<DateTimeSectionProps> = ({ slots, isLoadingSlots, slotsError, bookableDates, onViewMonthChange }) => {
  const { booking, updateBooking, durationHours } = useBookingContext();

  useEffect(() => {
    if (!booking.date) {
      // Only default to today when bookableDates is unknown (null = API failed / not yet loaded)
      // OR today is actually bookable. If the loaded schedule excludes today, leave the date
      // empty so the user picks an enabled day — otherwise this re-defaults after BookingCreate
      // clears an unbookable date, causing an infinite clear→default→clear loop.
      const todayIso = formatLocalDate(today);
      if (bookableDates === null || bookableDates.has(todayIso)) {
        updateBooking({ date: todayIso, startTime: '', endTime: '' });
      }
    }
  }, [booking.date, bookableDates, updateBooking]);

  useEffect(() => {
    if (!booking.startTime || !durationHours) {
      if (booking.endTime) {
        updateBooking({ endTime: '' });
      }
      return;
    }

    const nextEnd = addHoursToTime(booking.startTime, durationHours);
    if (booking.endTime !== nextEnd) {
      updateBooking({ endTime: nextEnd });
    }
  }, [booking.endTime, booking.startTime, durationHours, updateBooking]);

  const availableSlots = useMemo(() => {
    const fullMotionCount = booking.fullMotionSimulators;
    const esportsCount = booking.esportsSimulators;

    return slots.filter((slot) => {
      const fullMotionTotal = slot.fullMotion?.available ?? 0;
      const esportsTotal = slot.esports?.available ?? 0;
      return fullMotionTotal >= fullMotionCount && esportsTotal >= esportsCount;
    });
  }, [booking.esportsSimulators, booking.fullMotionSimulators, slots]);

  useEffect(() => {
    // Don't clear selected time while we're still loading availability;
    // otherwise returning from checkout can wipe a valid selection before slots arrive.
    if (isLoadingSlots || slotsError) return;

    if (booking.startTime && !availableSlots.some((s) => s.startTime === booking.startTime)) {
      updateBooking({ startTime: '', endTime: '' });
    }
  }, [availableSlots, booking.startTime, isLoadingSlots, slotsError, updateBooking]);

  return (
    <div>
      <label className="settings-field-label">Date:</label>
      <BookingCalendar
        selectedDate={booking.date}
        onSelectDate={(iso) => updateBooking({ date: iso, startTime: '', endTime: '' })}
        isDateDisabled={bookableDates ? (iso) => !bookableDates.has(iso) : undefined}
        onViewMonthChange={onViewMonthChange}
      />

      <label className="settings-field-label">Time:</label>
      {isLoadingSlots ? (
        <p className="text-paragraphs mt-1">Loading available time slots...</p>
      ) : !booking.isSimulatorSelected ? (
        <p className="text-paragraphs mt-1">Please choose a simulator type and quantity first.</p>
      ) : !booking.date ? (
        <p className="text-paragraphs mt-1">Please select a date to check availability.</p>
      ) : availableSlots.length === 0 ? (
        <div className="alert alert-warning mt-1" role="alert">
          No available slots for selected simulator type and quantity.
        </div>
      ) : (
        <div className="booking-time-slots mt-1">
          {availableSlots.map((slot) => (
            <button
              key={slot.startTime}
              type="button"
              className={`btn ${booking.startTime === slot.startTime ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() =>
                updateBooking({
                  startTime: slot.startTime,
                  endTime: durationHours ? addHoursToTime(slot.startTime, durationHours) : ''
                })
              }
            >
              <div>{formatUsTime(slot.startTime)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DateTimeSection;
